import React, { useEffect, useRef, useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { Brain, Loader2, Send, Paperclip, X, Sparkles, Lock, ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { privateHistory, privateSend, privateClear, nexusMemory } from "../lib/api";
import { toast } from "sonner";

export default function NexusPrivate({ userId }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [memory, setMemory] = useState("");
  const [showMem, setShowMem] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try { const r = await privateHistory(userId); setMessages(r.messages || []); }
    catch (e) { toast.error("Could not load your conversation"); }
    finally { setLoading(false); }
  }, [userId]);
  const loadMemory = useCallback(async () => { try { const r = await nexusMemory(userId); setMemory(r.memory || ""); } catch (e) {} }, [userId]);
  useEffect(() => { loadHistory(); loadMemory(); }, [loadHistory, loadMemory]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const send = async () => {
    if (!text.trim() && files.length === 0) return;
    const myText = text.trim();
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", text: myText, attachments: files.map((f) => ({ name: f.name })) }]);
    setText(""); const sentFiles = files; setFiles([]); setSending(true);
    try {
      const r = await privateSend(userId, myText, sentFiles);
      setMessages((m) => [...m, { id: r.id, role: "nexus", text: r.reply, attachments: [] }]);
      loadMemory();
    } catch (e) { toast.error("Nexus couldn't respond — try again"); setMessages((m) => m.filter((x) => !String(x.id).startsWith("tmp-"))); }
    finally { setSending(false); }
  };

  const clear = async () => { if (!window.confirm("Clear this private conversation? (Nexus keeps what he's learned about you.)")) return; try { await privateClear(userId); setMessages([]); toast.success("Cleared"); } catch (e) { toast.error("Could not clear"); } };

  return (
    <div className="lg:col-span-2 nx-glass rounded-2xl p-4 flex flex-col" style={{ minHeight: "60vh" }} data-testid="private-panel">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "radial-gradient(circle at 40% 30%, #00F0FF, #061018 75%)", boxShadow: "0 0 20px #00F0FF66" }}><Brain className="w-5 h-5 text-white" /></div>
        <div>
          <h2 className="nx-orbitron font-black tracking-[0.2em] text-[#00F0FF] text-base leading-none">NEXUS · PRIVATE</h2>
          <p className="text-cyan-100/45 text-[10px] nx-mono mt-0.5 flex items-center gap-1"><Lock className="w-3 h-3" /> End-to-end encrypted at rest · honest, logical counsel</p>
        </div>
        <button onClick={clear} data-testid="private-clear" title="Clear chat" className="ml-auto text-cyan-200/40 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
      </div>

      {/* memory */}
      <button onClick={() => setShowMem((v) => !v)} data-testid="private-memory-toggle" className="flex items-center gap-2 text-[11px] nx-mono text-[#00F0FF] mb-2">
        <Sparkles className="w-3.5 h-3.5" /> What Nexus knows about me {showMem ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {showMem && (
        <div data-testid="private-memory" className="mb-3 max-h-52 overflow-y-auto rounded-xl bg-[#0a1526] border border-[#00F0FF22] p-3 text-cyan-100/70 text-[12px] nx-mono whitespace-pre-wrap">
          {memory ? memory : "Nexus hasn't learned anything yet — start talking to him below."}
        </div>
      )}

      {/* messages */}
      <div className="flex-1 overflow-y-auto space-y-3 pr-1" data-testid="private-messages">
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>
        ) : messages.length === 0 ? (
          <div className="py-10 text-center text-cyan-100/30 text-sm nx-mono">Tell Nexus anything — a worry, a decision, your goals. Attach documents or photos and he'll study them. He learns, and he'll always be honest with you.</div>
        ) : messages.map((m) => (
          <div key={m.id} data-testid={`private-msg-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-[#00F0FF] text-[#030712]" : "bg-[#0a1526] border border-[#00F0FF22] text-cyan-50"}`}>
              {m.role === "nexus"
                ? <div className="nx-md text-[13px] leading-relaxed"><ReactMarkdown>{m.text}</ReactMarkdown></div>
                : <span className="whitespace-pre-wrap">{m.text}</span>}
              {(m.attachments || []).length > 0 && <div className="mt-1 text-[10px] opacity-70">📎 {m.attachments.map((a) => a.name).join(", ")}</div>}
            </div>
          </div>
        ))}
        {sending && <div className="flex justify-start"><div className="rounded-2xl px-4 py-2.5 bg-[#0a1526] border border-[#00F0FF22] text-cyan-200/60 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Nexus is thinking…</div></div>}
        <div ref={endRef} />
      </div>

      {/* composer */}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="private-attachments">
          {files.map((f, i) => (
            <span key={i} className="text-[11px] nx-mono bg-[#0a1526] border border-[#00F0FF33] rounded-full px-2.5 py-1 text-cyan-100/70 flex items-center gap-1">{f.name}<button onClick={() => setFiles((p) => p.filter((_, j) => j !== i))}><X className="w-3 h-3" /></button></span>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2 mt-2">
        <input ref={fileRef} type="file" multiple className="hidden" data-testid="private-file-input" onChange={(e) => { setFiles((p) => [...p, ...Array.from(e.target.files || [])]); e.target.value = ""; }} accept="image/*,.pdf,.docx" />
        <button onClick={() => fileRef.current?.click()} data-testid="private-attach" className="p-2.5 rounded-xl text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] shrink-0"><Paperclip className="w-5 h-5" /></button>
        <textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={1} data-testid="private-input"
          placeholder="Talk to Nexus…" className="flex-1 resize-none bg-[#0a1526] text-cyan-50 border border-[#00F0FF33] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF] max-h-32" />
        <button onClick={send} disabled={sending} data-testid="private-send" className="p-2.5 rounded-xl text-[#030712] bg-[#00F0FF] disabled:opacity-40 shrink-0"><Send className="w-5 h-5" /></button>
      </div>
    </div>
  );
}
